import { useState } from "react";

import { IntentPanel } from "./IntentPanel";
import { CreateTermPanel } from "./panels/CreateTermPanel";
import { DisambiguationPanel } from "./panels/DisambiguationPanel";
import { ForbiddenFormPanel } from "./panels/ForbiddenFormPanel";
import { HardTranslationPanel } from "./panels/HardTranslationPanel";
import { ProbePanel } from "./panels/ProbePanel";
import { SynonymPanel } from "./panels/SynonymPanel";
import { TermOverviewPanel } from "./panels/TermOverviewPanel";
import type { IntentId } from "./senseSearchTypes";

interface IntentDef {
  id: IntentId;
  title: string;
  example: string;
  subtitle: string;
  ready: boolean;
}

const INTENTS: IntentDef[] = [
  {
    id: "hard",
    title: "…чтобы это всегда переводилось так",
    example: "всегда «оральные антикоагулянты», а не «пероральные»",
    subtitle: "Зафиксировать перевод — переводчик будет обязан его использовать.",
    ready: true,
  },
  {
    id: "forbidden",
    title: "…запретить неправильный перевод",
    example: "запретить «ППАК», всегда заменять на «ПОАК»",
    subtitle:
      "Если модель иногда выдаёт неверную форму — запретите её, заменим на правильную.",
    ready: true,
  },
  {
    id: "synonym",
    title: "…разрешить ещё допустимый вариант",
    example: "принимать «acute ischemic stroke» как валидный для ОНМК",
    subtitle:
      "Пометить альтернативу синонимом, чтобы проверка не считала её ошибкой.",
    ready: true,
  },
  {
    id: "disambiguation",
    title: "…развести смыслы аббревиатуры",
    example: "HR → ОР (hazard ratio) vs ЧСС (heart rate)",
    subtitle:
      "У аббревиатуры несколько смыслов — научите систему выбирать правильный по контексту.",
    ready: true,
  },
  {
    id: "create",
    title: "…добавить новый термин/аббревиатуру",
    example: "NOAC → НОАК",
    subtitle: "Завести запись с нуля.",
    ready: true,
  },
  {
    id: "probe",
    title: "…проверить, как переведётся фраза",
    example: "вставь предложение с «HR» — увидишь, какой смысл сработает",
    subtitle: "Проверить перевод прямо сейчас, ничего не меняя в словаре.",
    ready: true,
  },
];

const OVERVIEW: IntentDef = {
  id: "overview",
  title: "…посмотреть и поправить, что уже настроено для термина",
  example: "найди термин — увидишь перевод, запреты, синонимы, смыслы",
  subtitle: "Единое место, чтобы посмотреть и отменить настройки термина.",
  ready: true,
};

export function IntentLauncher() {
  const [open, setOpen] = useState<IntentId | null>(null);
  const active =
    open === OVERVIEW.id
      ? OVERVIEW
      : (INTENTS.find((i) => i.id === open) ?? null);

  function card(def: IntentDef) {
    const isOpen = open === def.id;
    return (
      <button
        key={def.id}
        type="button"
        className="mt-card mt-card--clickable"
        style={{
          textAlign: "left",
          padding: 12,
          border: isOpen ? "2px solid var(--accent, #2563eb)" : undefined,
          opacity: def.ready ? 1 : 0.65,
        }}
        onClick={() => setOpen(isOpen ? null : def.id)}
      >
        <div style={{ fontWeight: 600 }}>
          {def.title}
          {!def.ready && (
            <span
              className="mt-badge mt-badge--no-dot"
              style={{ marginLeft: 6 }}
            >
              скоро
            </span>
          )}
        </div>
        <div
          className="mt-muted"
          style={{ marginTop: 4, fontSize: "var(--fs-sm)" }}
        >
          Пример: {def.example}
        </div>
      </button>
    );
  }

  function body(id: IntentId) {
    switch (id) {
      case "forbidden":
        return <ForbiddenFormPanel />;
      case "disambiguation":
        return <DisambiguationPanel />;
      case "overview":
        return <TermOverviewPanel />;
      case "probe":
        return <ProbePanel />;
      case "hard":
        return <HardTranslationPanel />;
      case "synonym":
        return <SynonymPanel />;
      case "create":
        return <CreateTermPanel />;
    }
  }

  return (
    <section
      className="rf-section"
      style={{
        marginBottom: "var(--sp-3)",
        border: "1px solid var(--blue-500)",
        background: "var(--blue-50)",
        boxShadow: "0 1px 6px rgba(37, 99, 235, 0.12)",
      }}
    >
      <div
        className="rf-section__head"
        style={{
          background: "var(--blue-500)",
          color: "#fff",
          borderBottomColor: "var(--blue-500)",
        }}
      >
        <span>Я хочу…</span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 10,
          }}
        >
          {INTENTS.map(card)}
        </div>
        <div style={{ marginTop: 10 }}>{card(OVERVIEW)}</div>
        {active && (
          <IntentPanel
            title={active.title}
            subtitle={active.subtitle}
            onClose={() => setOpen(null)}
          >
            {body(active.id)}
          </IntentPanel>
        )}
      </div>
    </section>
  );
}
